import React, { useEffect, useState } from 'react';
import { FileText, Download, Loader2 } from 'lucide-react';
import api from '../config/api';
import toast from 'react-hot-toast';

const DOC_TYPES: { type: string; label: string; description: string }[] = [
  { type: 'fee_statement', label: 'Fee Statement', description: 'Payment history and outstanding balance' },
  { type: 'id_card', label: 'Student ID Card', description: 'Compact identity card layout' },
  { type: 'completion_certificate', label: 'Completion Certificate', description: 'Formatted award certificate' },
  { type: 'transcript_pdf', label: 'Academic Transcript', description: 'Unit grades and cumulative GPA' },
];

export default function DocumentsPanel({ studentId }: { studentId: string }) {
  const [documents, setDocuments] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState<string | null>(null);
  const [downloading, setDownloading] = useState<string | null>(null);

  const fetchDocuments = () => {
    setLoading(true);
    api.get('/documents', { params: { student_id: studentId } })
      .then(r => setDocuments(r.data.data))
      .finally(() => setLoading(false));
  };

  useEffect(fetchDocuments, [studentId]); // eslint-disable-line react-hooks/exhaustive-deps

  const generate = async (type: string) => {
    setGenerating(type);
    try {
      await api.post('/documents/generate', { type, student_id: studentId });
      toast.success('Document generated');
      fetchDocuments();
    } finally { setGenerating(null); }
  };

  const download = async (doc: any) => {
    setDownloading(doc.id);
    try {
      const r = await api.get(`/documents/${doc.id}/download`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([r.data], { type: 'application/pdf' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = `${doc.type}-${doc.id.slice(0, 8)}.pdf`;
      a.click();
      window.URL.revokeObjectURL(url);
    } finally { setDownloading(null); }
  };

  return (
    <div className="card">
      <h3 style={{ fontWeight: 600, marginBottom: 4 }}>Documents</h3>
      <p style={{ color: 'var(--text-muted)', fontSize: 13, marginBottom: 16 }}>Generate official documents as PDF</p>

      <div className="grid-2" style={{ gap: 12, marginBottom: 24 }}>
        {DOC_TYPES.map(d => (
          <div key={d.type} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', border: '1px solid var(--border)', borderRadius: 8, padding: '12px 14px' }}>
            <div>
              <div style={{ fontWeight: 600, fontSize: 14 }}>{d.label}</div>
              <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>{d.description}</div>
            </div>
            <button className="btn btn-secondary btn-sm" disabled={generating === d.type} onClick={() => generate(d.type)}>
              {generating === d.type ? <Loader2 size={14} style={{ animation: "spin 0.7s linear infinite" }} /> : <FileText size={14} />}
              Generate
            </button>
          </div>
        ))}
      </div>

      <h4 style={{ fontWeight: 600, marginBottom: 10, fontSize: 14 }}>Generated Documents</h4>
      {loading ? (
        <div className="loader loader-dark" />
      ) : documents.length === 0 ? (
        <div className="empty-state">No documents generated yet.</div>
      ) : (
        <div className="table-container">
          <table>
            <thead><tr><th>Type</th><th>Generated</th><th>Status</th><th></th></tr></thead>
            <tbody>
              {documents.map((d: any) => (
                <tr key={d.id}>
                  <td>{d.label}</td>
                  <td>{new Date(d.generated_at).toLocaleString()}</td>
                  <td><span className={`badge badge-${d.is_valid ? 'success' : 'gray'}`}>{d.is_valid ? 'Valid' : 'Invalid'}</span></td>
                  <td>
                    <button className="btn btn-secondary btn-sm" disabled={downloading === d.id} onClick={() => download(d)}>
                      {downloading === d.id ? <Loader2 size={14} style={{ animation: "spin 0.7s linear infinite" }} /> : <Download size={14} />}
                      Download
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
